import React, { useMemo } from "react";

import { Box } from "@mui/material";

import { Error } from "../../../../models/error";
import { CommercialInvoice, Shipment } from "../../../../models/shipments";
import { ViewOnly } from "../../../../types";
import { ValueOf } from "../../../Generic/Estimate/types";
import { useBookingResourcesContext } from "../../hooks/useResourcesContext";
import { CommercialInvoiceAlternative } from "./Alternative";
import { CommercialInvoiceExporterInformation } from "./ExporterInformation";
import { CommercialInvoiceGeneral } from "./General";
import useStyles from "./styles";
import { CommercialInvoiceKey } from "./types";

interface DetailsProps {
  shipment?: Shipment;
  updateShipment: (attribute: string, value: any, objectKey?: string) => void;
  cancelShipment: (id: number) => void;
  viewOnly: ViewOnly;
  fieldHasError: (field: string) => boolean;
  getErrorByField: (field: string) => string | undefined;
  isBangladesh?: boolean;
  loadingBooking?: boolean;
  errorResponse?: Error;
}

export const CommercialInvoiceDetails = ({
  shipment,
  updateShipment,
  viewOnly: viewMode = { fields: false, actions: false },
  fieldHasError,
  getErrorByField,
  loadingBooking,
  isBangladesh,
  errorResponse,
}: DetailsProps) => {
  const classes = useStyles();
  const { portList } = useBookingResourcesContext();

  const { fields: viewOnly } = viewMode;
  const portOfLoadingCountryCode = useMemo(
    () =>
      portList.find((port) => port.id === shipment?.portOfLoadingId)?.country?.code ??
      "countryCode",
    [portList, shipment?.portOfLoadingId]
  );

  const commercialInvoice = shipment?.commercialInvoice;

  const updateComercialInvoice = (
    key: CommercialInvoiceKey,
    value: ValueOf<CommercialInvoice> | null
  ) => {
    updateShipment("commercialInvoice", { ...shipment?.commercialInvoice, [key]: value }, key);
  };

  return (
    <div
      id={portOfLoadingCountryCode}
      data-cy="shipments-commercial-invoice-box"
      className={classes.mainDiv}
    >
      <Box display="flex" flexDirection={"column"} paddingTop={1}>
        <CommercialInvoiceGeneral
          loadingBooking={loadingBooking}
          commercialInvoice={commercialInvoice}
          viewOnly={viewOnly ?? false}
          fieldHasError={fieldHasError}
          getErrorByField={getErrorByField}
          updateComercialInvoice={updateComercialInvoice}
        />
        {isBangladesh && (
          <CommercialInvoiceAlternative
            loadingBooking={loadingBooking}
            commercialInvoice={commercialInvoice}
            viewOnly={viewOnly ?? false}
            fieldHasError={fieldHasError}
            getErrorByField={getErrorByField}
            updateComercialInvoice={updateComercialInvoice}
            errorResponse={errorResponse}
          />
        )}
        <CommercialInvoiceExporterInformation
          loadingBooking={loadingBooking}
          commercialInvoice={commercialInvoice}
          viewOnly={viewOnly ?? false}
          fieldHasError={fieldHasError}
          getErrorByField={getErrorByField}
          updateComercialInvoice={updateComercialInvoice}
        />
      </Box>
    </div>
  );
};
